/**
 * useTabParam — URL-driven tab state hook.
 *
 * Stores the active tab in a URL query parameter so the user's tab selection
 * is preserved on navigation, bookmarking, and sharing.
 *
 * Usage:
 *   const { tab, setTab } = useTabParam("overview");
 *   // URL: /organizations/acme-corp?tab=members
 */

import { useCallback } from "react";

import { useSearchParams } from "react-router";

interface UseTabParamReturn<T extends string> {
  tab: T;
  setTab: (tab: T) => void;
}

/**
 * @param defaultTab  The tab value to use when no query param is present.
 * @param paramName   The query parameter name (default: "tab").
 */
export function useTabParam<T extends string>(
  defaultTab: T,
  paramName = "tab",
): UseTabParamReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get(paramName) as T) || defaultTab;

  const setTab = useCallback(
    (newTab: T) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newTab === defaultTab) {
            next.delete(paramName);
          } else {
            next.set(paramName, newTab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaultTab, paramName],
  );

  return { tab, setTab };
}
